import { useCategoryTreeItem } from '../categoryTree';
import { TypeOfApplicationLink, UIDAPresentationTypeEnum, tvpil } from '../compat/tvpil';

import { ImageSize, useImageSettings } from './useImageSettings';

export type UseRailSettingsProps = {
  imageSize?: ImageSize;
  referenceId: string;
};

export function useRailSettings({ referenceId, imageSize }: UseRailSettingsProps) {
  const category = useCategoryTreeItem(referenceId);

  const imageSettings = useImageSettings({ referenceId, imageSize });

  if (!category) {
    return {
      settings: undefined,
    };
  }

  const inAppLink = category.link?.targetGridCategoryId ?? category.link?.inAppLink;
  const presentShowMore = Boolean(category.railProperty.presentShowMore);

  return {
    settings: {
      branding: category.uiDesignElements,
      id: category.id,
      ...imageSettings,
      isTopShelf: Boolean(category.railProperty.showTopShelfRailUi),
      moreLink:
        presentShowMore && inAppLink
          ? createMenuLink(referenceId, category.subtype, inAppLink, category.link.typeOfAppLink)
          : undefined,
      parentalPinProtected: Boolean(category.parentalPinProtected),
      presentEntitlementIndicator: Boolean(category.asideProperty?.presentEntitlementIndicator),
      presentRating: Boolean(category.asideProperty?.presentRating),
      title: category.name,
    },
  };
}

function createMenuLink(
  referenceId: string,
  subtype?: UIDAPresentationTypeEnum,
  inAppLink?: string,
  linkType?: TypeOfApplicationLink,
) {
  if (!inAppLink || !linkType) {
    return undefined;
  }

  const linkSubtype = subtype && tvpil.isNewSubtypeSupported(subtype) ? subtype : undefined;

  return {
    inAppLink: String(inAppLink),
    link: tvpil.generateMenuLink(linkType, inAppLink, referenceId, linkSubtype),
    linkType,
    referenceId,
  };
}

export type RailSettings = NonNullable<ReturnType<typeof useRailSettings>['settings']>;
